import { faker } from '@faker-js/faker';
import { useQuery } from '@tanstack/react-query';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type {
  GetProgressReportsParams,
  ProgressModuleReport,
  ProgressReportsResponse,
} from './types';

export const getProgressReports = (
  params: GetProgressReportsParams
): Promise<ProgressReportsResponse> => {
  return axios.get('/user/v1/progress-reports', { params });
};

export const progressReportsKeys = {
  all: ['progress-reports'] as const,
  byProduct: (productId: string) =>
    [...progressReportsKeys.all, productId] as const,
};

const useProgressReportsQuery = (params: GetProgressReportsParams) => {
  return useQuery({
    queryKey: progressReportsKeys.byProduct(params.product_id),
    queryFn: () => getProgressReports(params),
    enabled: !!params.product_id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useProgressReportsData = (productId: string) => {
  const query = useProgressReportsQuery({ product_id: productId });
  const firstProduct = query.data?.data?.[0];
  const overallReport = firstProduct?.overall_report;
  const moduleReports = firstProduct?.module_reports ?? [];

  // Edge case 2: overall_report has a report_url (treat it as downloadable regardless of report_status)
  const overallReportUrl = overallReport?.report_url ?? null;

  // Edge case 1: overall_report exists but has no report_url, and module_reports has entries
  const hasOverallReportWithoutUrl =
    overallReport !== undefined &&
    !overallReport.report_url &&
    moduleReports.length > 0;

  return {
    overallReportUrl,
    hasOverallReportWithoutUrl,
    modulesCompleted: overallReport?.modules_completed ?? null,
    moduleReports,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

// ── MSW mock ──────────────────────────────────────────────

const MODULE_NAMES = [
  'Number Theory',
  'Algebra Fundamentals',
  'Geometry Basics',
  'Fractions and Decimals',
  'Statistics and Probability',
  'Coordinate Geometry',
  'Quadratic Equations',
  'Trigonometry',
  'Linear Programming',
  'Calculus Introduction',
];

const generateModuleReport = (index: number): ProgressModuleReport => ({
  module_id: faker.string.uuid(),
  module_sequence: String(index),
  module_name: MODULE_NAMES[(index - 1) % MODULE_NAMES.length],
  report_url: `https://static.bhanzu.com/module_reports/${faker.string.uuid()}/${faker.string.uuid()}.pdf`,
});

export const getProgressReportsMockHandler = http.get(
  `${env.API_URL}/user/v1/progress-reports`,
  async (): Promise<HttpResponse<ProgressReportsResponse>> => {
    await networkDelay();

    const modulesCompleted = faker.number.int({ min: 5, max: 15 });
    const moduleReportsCount = faker.number.int({
      min: 0,
      max: modulesCompleted,
    });

    return HttpResponse.json({
      status: 'SUCCESS',
      data: [
        {
          product_id: faker.string.uuid(),
          overall_report: {
            report_url: `https://static.bhanzu.com/progress-reports-ind-us/${faker.string.uuid()}.pdf`,
            report_status: 'active',
            modules_completed: modulesCompleted,
          },
          module_reports: Array.from({ length: moduleReportsCount }, (_, i) =>
            generateModuleReport(i + 1)
          ),
        },
      ],
      message: 'Student progress reports fetched successfully',
    });
  }
);
